import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import api from './api';
import localStorageAccess from '../common/helpers/localStorageAccess';
import { addErrorNotification } from '../common/notification/notificationReducer';
import type { AppDispatch, AuthenticationState, Store } from '../store';

const initialState: AuthenticationState = {
  isInitialized: false,
  isLoading: false,
  isInitLoading: false,
  isLoginLoading: false,
  isAuthenticated: false,
  salt: null,
  errorMessage: null,
};

const isInitialized = createAsyncThunk<
  boolean,
  undefined,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('authentication/isInitialized', async (_, { dispatch, rejectWithValue }) => {
  const response = await api.isInitialized();
  if (response != null) {
    return response.isInitialized;
  }
  dispatch(addErrorNotification('Beim abrufen der Initialisierung ist ein Fehler aufgetreten.'));
  return rejectWithValue('Beim abrufen der Initialisierung ist ein Fehler aufgetreten.');
});

const initialize = createAsyncThunk<
  boolean,
  { password: string; salt: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('authentication/initialize', async ({ password, salt }, { dispatch, rejectWithValue }) => {
  const response = await api.initialize(password, salt);
  if (response != null) {
    return response.isInitialized;
  }
  dispatch(addErrorNotification('Beim Initialisieren ist ein Fehler aufgetreten.'));
  return rejectWithValue('Beim Initialisieren ist ein Fehler aufgetreten.');
});

const loadSalt = createAsyncThunk<
  string | null,
  undefined,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('authentication/loadSalt', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  if (state.authentication.isInitialized) {
    const response = await api.fetchSalt();
    if (response != null) {
      return response.salt;
    }
    dispatch(addErrorNotification('Beim abrufen des Salts ist ein Fehler aufgetreten.'));
    return rejectWithValue('Beim abrufen des Salts ist ein Fehler aufgetreten.');
  }
  return null;
});

const authenticate = createAsyncThunk<
  string,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('authentication/authenticate', async (password: string, { getState, rejectWithValue }) => {
  const state = getState();
  if (state.authentication.isInitialized) {
    const response = await api.login(password);
    if (response != null) {
      localStorageAccess.setJwtToken(response.token);
      return response.token;
    }
    return rejectWithValue('Das Passwort ist ungültig.');
  }
  return '';
});

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(isInitialized.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(isInitialized.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = action.payload;
      })
      .addCase(isInitialized.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload;
      })
      .addCase(initialize.pending, (state) => {
        state.isInitLoading = true;
      })
      .addCase(initialize.fulfilled, (state, action) => {
        state.isInitLoading = false;
        state.isInitialized = action.payload;
      })
      .addCase(initialize.rejected, (state, action) => {
        state.isInitLoading = false;
        state.errorMessage = action.payload;
      })
      .addCase(loadSalt.pending, (state) => {
        state.isLoginLoading = true;
      })
      .addCase(loadSalt.fulfilled, (state, action) => {
        state.isLoginLoading = false;
        state.salt = action.payload;
      })
      .addCase(loadSalt.rejected, (state, action) => {
        state.isLoginLoading = false;
        state.errorMessage = action.payload;
      })
      .addCase(authenticate.pending, (state) => {
        state.isLoading = true;
        state.isAuthenticated = false;
      })
      .addCase(authenticate.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = true;
      })
      .addCase(authenticate.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.errorMessage = action.payload;
      })
      .addCase('resetState', (state) => {
        state = initialState;
      });
  },
});

export { authenticate, isInitialized, initialize, loadSalt };

export default authenticationSlice.reducer;
